import { imageUrl } from "@a2z/ui/media/image-url";
import type { ProductImageView } from "@/lib/api/types";
import { MEDIA_BASE_URL } from "@/lib/media-base";

import type {
  ImageItem,
  ImageItemBgRemoval,
  MoveDirection,
  UploadedImageMap,
  ViewerItem,
} from "./types";

const CARD_WIDTH = 480;
const DETAIL_WIDTH = 1440;

const maxUploadBytes = 10 * 1024 * 1024;
const maxSide = 6000;
const maxPixels = 30_000_000;

function mapBgRemoval(image: ProductImageView): ImageItemBgRemoval | undefined {
  // With CF Image Transformations, bg-removed variants are generated on
  // demand by the helper (segment=foreground). There is no async lifecycle
  // any more — every uploaded image is immediately "ready" and the seller's
  // per-image preference is read from `useBgRemoved`. The legacy
  // `bgRemovalStatus` field is ignored on the frontend.
  if (!image.objectKey) {
    return undefined;
  }
  return {
    state: "ready",
    cleanCardUrl: imageUrl(
      image.objectKey,
      { width: CARD_WIDTH, format: "auto", removeBackground: true },
      { baseUrl: MEDIA_BASE_URL },
    ),
    cleanDetailUrl: imageUrl(
      image.objectKey,
      { width: DETAIL_WIDTH, format: "auto", removeBackground: true },
      { baseUrl: MEDIA_BASE_URL },
    ),
    activeVersion: image.useBgRemoved ? "clean" : "original",
  };
}

export const mapRemoteImages = (images?: ProductImageView[]): ImageItem[] =>
  (images ?? []).map((image) => ({
    id: image.id,
    kind: "remote",
    sortOrder: image.sortOrder,
    createdAt: image.createdAt,
    updatedAt: image.updatedAt,
    productId: image.productId,
    objectKey: image.objectKey,
    status: image.status,
    objectGeneration: image.objectGeneration,
    bgRemoval: mapBgRemoval(image),
  }));

export const buildImageSignature = (items: ImageItem[]) =>
  [...items]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((image) => {
      const fileName = image.file?.name ?? "";
      // Include bgRemoval state + activeVersion so toggling Original/Clean triggers
      // hasUnsavedChanges and shows the save button, and so refetches propagate correctly.
      const bgKey = `${image.bgRemoval?.state ?? "idle"}:${image.bgRemoval?.activeVersion ?? ""}`;
      return `${image.kind}:${image.id}:${image.sortOrder}:${image.objectKey ?? ""}:${fileName}:${bgKey}`;
    })
    .join("|");

const readImageMeta = (file: File) =>
  new Promise<{ width: number; height: number }>((resolve, reject) => {
    const img = new window.Image();
    const objectUrl = URL.createObjectURL(file);
    img.decoding = "async";
    img.onload = () => {
      const width = img.naturalWidth || 0;
      const height = img.naturalHeight || 0;
      URL.revokeObjectURL(objectUrl);
      resolve({ width, height });
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("invalidImage"));
    };
    img.src = objectUrl;
  });

export const validateImageFile = async (file: File) => {
  if (file.size > maxUploadBytes) {
    throw new Error("fileTooLarge");
  }
  const { width, height } = await readImageMeta(file);
  if (!width || !height) {
    throw new Error("invalidImage");
  }
  const side = Math.max(width, height);
  if (side > maxSide || width * height > maxPixels) {
    throw new Error("imageTooLarge");
  }
};

const buildCardUrl = (image: ImageItem): string | null => {
  if (!image.objectKey) {
    return image.previewUrl ?? null;
  }
  const useBgRemoved =
    image.bgRemoval?.activeVersion === "clean";
  return imageUrl(
    image.objectKey,
    { width: CARD_WIDTH, format: "auto", removeBackground: useBgRemoved },
    { baseUrl: MEDIA_BASE_URL },
  );
};

const buildDetailUrl = (image: ImageItem): string | null => {
  if (!image.objectKey) {
    return image.previewUrl ?? null;
  }
  const useBgRemoved =
    image.bgRemoval?.activeVersion === "clean";
  return imageUrl(
    image.objectKey,
    { width: DETAIL_WIDTH, format: "auto", removeBackground: useBgRemoved },
    { baseUrl: MEDIA_BASE_URL },
  );
};

export const resolvePreviewUrl = (image: ImageItem) => {
  if (image.kind === "local") {
    return image.previewUrl ?? null;
  }
  return buildCardUrl(image) ?? image.previewUrl ?? null;
};

export const resolveDetailUrl = (image: ImageItem) => {
  if (image.kind === "local") {
    return image.previewUrl ?? null;
  }
  return buildDetailUrl(image) ?? image.previewUrl ?? null;
};

export const buildViewerItems = (images: ImageItem[]): ViewerItem[] =>
  images
    .map((image) => ({
      id: image.id,
      url: resolveDetailUrl(image) ?? resolvePreviewUrl(image),
    }))
    .filter((item): item is ViewerItem => Boolean(item.url));

export const createLocalId = () => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `local-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const reconcileSavedImages = (
  orderedImages: ImageItem[],
  uploadedImages: UploadedImageMap
): ImageItem[] =>
  orderedImages.map((image, index) => {
    if (image.kind === "local") {
      const uploaded = uploadedImages.get(image.id);
      if (!uploaded) {
        return image;
      }
      return {
        ...image,
        id: uploaded.imageId,
        objectKey: uploaded.objectKey,
        kind: "remote",
        sortOrder: index,
        status: image.status ?? "UPLOADING",
        previewUrl: image.previewUrl,
        file: undefined,
      };
    }
    return {
      ...image,
      sortOrder: index,
    };
  });

export const mergeImagesWithPreviewFallback = (
  remoteImages: ImageItem[],
  previewSourceImages: ImageItem[]
): ImageItem[] =>
  remoteImages.map((image) => {
    if (resolvePreviewUrl(image)) {
      return image;
    }

    const fallback = previewSourceImages.find((candidate) => {
      if (image.objectKey && candidate.objectKey) {
        return image.objectKey === candidate.objectKey;
      }
      return image.id === candidate.id;
    });

    if (!fallback?.previewUrl) {
      return image;
    }

    return {
      ...image,
      previewUrl: fallback.previewUrl,
    };
  });

export const moveImageByDirection = (
  current: ImageItem[],
  id: string,
  direction: MoveDirection
): ImageItem[] => {
  const sortedCurrent = [...current].sort((a, b) => a.sortOrder - b.sortOrder);
  const index = sortedCurrent.findIndex((image) => image.id === id);
  if (index < 0) {
    return current;
  }

  const next = [...sortedCurrent];
  const targetIndex = direction === "up" ? index - 1 : index + 1;
  if (targetIndex < 0 || targetIndex >= next.length) {
    return current;
  }

  const temp = next[index];
  next[index] = next[targetIndex];
  next[targetIndex] = temp;

  return next.map((image, idx) => ({
    ...image,
    sortOrder: idx,
  }));
};

/**
 * Move an image to a specific position, shifting all other items to
 * accommodate. Used by the "make cover" action in the hybrid photo layout.
 */
export const moveImageToPosition = (
  current: ImageItem[],
  id: string,
  targetIndex: number,
): ImageItem[] => {
  const sortedCurrent = [...current].sort((a, b) => a.sortOrder - b.sortOrder);
  const fromIndex = sortedCurrent.findIndex((image) => image.id === id);
  if (fromIndex < 0) {
    return current;
  }
  const clampedTarget = Math.max(
    0,
    Math.min(targetIndex, sortedCurrent.length - 1),
  );
  if (fromIndex === clampedTarget) {
    return current;
  }
  const next = [...sortedCurrent];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(clampedTarget, 0, moved);
  return next.map((image, idx) => ({
    ...image,
    sortOrder: idx,
  }));
};

export type PendingUploadsError =
  | {
      kind: "request";
      message?: string;
    }
  | {
      kind: "generic";
    };

export const uploadPendingImages = async ({
  pendingImages,
  productId,
  createUploadIntent,
  uploadToSignedUrl,
}: {
  pendingImages: ImageItem[];
  productId: string;
  createUploadIntent: (input: {
    fileName: string;
    contentType: string;
    sizeBytes: number;
    productId: string;
  }) => Promise<{
    uploadUrl: string;
    imageId: string;
    objectKey: string;
  }>;
  uploadToSignedUrl: (input: {
    uploadUrl: string;
    file: Blob;
    contentType?: string;
  }) => Promise<unknown>;
}): Promise<UploadedImageMap> => {
  const uploadedImages: UploadedImageMap = new Map();

  for (const image of pendingImages) {
    if (!image.file) {
      throw {
        kind: "generic",
      } satisfies PendingUploadsError;
    }

    let uploadPayload: {
      uploadUrl: string;
      imageId: string;
      objectKey: string;
    };
    try {
      uploadPayload = await createUploadIntent({
        fileName: image.file.name,
        contentType: image.file.type,
        sizeBytes: image.file.size,
        productId,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : undefined;
      if (message) {
        throw {
          kind: "request",
          message,
        } satisfies PendingUploadsError;
      }
      throw {
        kind: "generic",
      } satisfies PendingUploadsError;
    }

    try {
      await uploadToSignedUrl({
        uploadUrl: uploadPayload.uploadUrl,
        file: image.file,
        contentType: image.file.type,
      });
    } catch {
      throw {
        kind: "generic",
      } satisfies PendingUploadsError;
    }

    uploadedImages.set(image.id, {
      imageId: uploadPayload.imageId,
      objectKey: uploadPayload.objectKey,
    });
  }

  return uploadedImages;
};

export const buildImagesPayload = (
  orderedImages: ImageItem[],
  uploadedImages: UploadedImageMap
) =>
  orderedImages.map((image, index) => {
    if (image.kind === "local") {
      const uploaded = uploadedImages.get(image.id);
      if (!uploaded) {
        return null;
      }
      return {
        imageId: uploaded.imageId,
        objectKey: uploaded.objectKey,
        sortOrder: index,
      };
    }

    // Always send the original objectKey for ownership validation.
    // Persist the seller's version choice via useBgRemoved so the server
    // knows which variant to serve as the primary image.
    const bgRemoval = image.bgRemoval;
    const useBgRemoved =
      bgRemoval?.state === "ready" && bgRemoval.activeVersion === "clean";

    return {
      imageId: image.id,
      objectKey: image.objectKey ?? "",
      sortOrder: index,
      useBgRemoved,
    };
  });
